import { AccountCategory, BookingSystem, CourseIntensityDto, CourseProduct, ProductCategory, Languages, UnitType } from 'app/shared/model';
import { FormError } from 'app/shared/model/Form';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as api from 'app/shared/api';
import { DivisionId } from 'app/shared/division/getDivisionId';
import {
  setFormErrors,
  getCourseProductById,
  setCourseProductName,
  setCourseProductIntensity,
  listCourseProductsData,
  filterData,
} from '../actions';
import {
  getCourseById,
  getCourseIntensity,
  getProductCategories,
  getAccountCategories,
  getLanguages,
  getUnitTypes,
} from '../selectors';
import { filterUnitTypesForCourse } from 'app/shared/utils/courseUnitTypes';
import { PRODUCT_ACTIVE_PACKAGE_TOOLTIP } from 'app/shared/api/productDtoHelpers';
import { useActivePackageDeactivationLock } from '../hooks/useActivePackageDeactivationLock';
import ActiveStatusFormItem from 'app/shared/components/Form/ActiveStatusFormItem';

import {
  Form,
  Input,
  Button,
  Select,
  message,
  Card,
  Row,
  Col,
  Checkbox,
  InputNumber,
  Tooltip,
} from 'antd';
import { SearchOutlined } from "@ant-design/icons";

interface CourseFormProps {
  editMode?: boolean;
  onSuccess?: () => void;
}

const COURSE_PRODUCT_TYPE_ID = 1;
const formatHours = (h: number) => {
  if (!Number.isFinite(h)) return "0";
  return Number.isInteger(h) ? String(h) : String(Math.round(h * 10) / 10);
};

const calcLessons = (i: CourseIntensityDto) => {
  const fromServer = Number(i.numberOfLessons ?? 0);
  if (fromServer > 0) return fromServer;

  const cores = Number(i.coreLessonsPerWeek ?? 0);
  const electives = Number(i.electiveLessonsPerWeek ?? 0);
  return Math.max(0, cores + electives);
};

const calcHours = (i: CourseIntensityDto) => {
  const fromServer = Number(i.numberOfHours ?? 0);
  if (fromServer > 0) return fromServer;

  const lessons = calcLessons(i);
  const len = Number(i.lessonLengthInMinutes ?? 0);
  if (lessons > 0 && len > 0) return (lessons * len) / 60;
  return 0;
};

const intensityMeta = (i: CourseIntensityDto) => {
  const lessons = calcLessons(i);
  const hours = calcHours(i);
  const cores = Number(i.coreLessonsPerWeek ?? 0);
  const electives = Number(i.electiveLessonsPerWeek ?? 0);
  const len = Number(i.lessonLengthInMinutes ?? 0);

  return `${lessons} lessons • ${formatHours(hours)} h • ${cores} cores • ${electives} electives • ${len} min`;
};

const normalizeOptionalInt = (v: any): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const AddEditCourseForm: React.FC<CourseFormProps> = ({ editMode, onSuccess }) => {
  const dispatch = useDispatch<any>();
  const courseProduct = useSelector(getCourseById)!;
  const courseIntensity = useSelector(getCourseIntensity);
  const productCategories = useSelector(getProductCategories) || [];
  const accountCategories = useSelector(getAccountCategories) || [];
  const languageCategories = useSelector(getLanguages) || [];
  const unitTypes = useSelector(getUnitTypes) || [];
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = React.useState(false);
  const [bookingSystems, setBookingSystems] = React.useState<BookingSystem[]>([]);
  const [isExclusionLoading, setIsExclusionLoading] = React.useState(false);
  const [initialExcludedSystems, setInitialExcludedSystems] = React.useState<string[]>([]);
  const lastCourseProductIdRef = React.useRef<number | undefined>(undefined);
  const productCategoryOptions = productCategories.map(i => ({ label: i.name, value: i.id }));
  const accountCategoryOptions = accountCategories.map(i => ({ label: i.name, value: i.id }));
  //const intensityOptions = courseIntensity.filter((i) => i.id > 0).map((i) => ({ label: i.name, value: Number(i.id) }));
  const languageOptions = languageCategories.map(i => ({ label: i.name, value: String(i.id) }));

  const intensityOptions = React.useMemo(
      () =>
          courseIntensity
              .filter(i => i.id > 0)
              .map(i => ({
                label: i.name,
                value: Number(i.id),
                meta: intensityMeta(i),
              })),
      [courseIntensity]
  );

  const courseUnitTypeOptions = React.useMemo(() => {
    const filtered = filterUnitTypesForCourse(unitTypes);
    const uid = courseProduct?.unitTypeId;
    if (uid != null && Number(uid) > 0 && !filtered.some(u => Number(u.id) === Number(uid))) {
      const label = (courseProduct?.unitTypeText ?? "").trim() || `Unit type ${uid}`;
      return [...filtered, { id: Number(uid), name: label }];
    }
    return filtered;
  }, [unitTypes, courseProduct?.unitTypeId, courseProduct?.unitTypeText]);

  const courseProductId = Number(courseProduct?.id ?? 0);
  const unitTypeLocked = Boolean(
    editMode &&
    courseProductId > 0 &&
    courseProduct?.hasActiveOffering === true
  );
  const preserveUnitTypeOnSave = unitTypeLocked;
  const { activeDeactivationLocked } = useActivePackageDeactivationLock(
    courseProduct,
    COURSE_PRODUCT_TYPE_ID,
    DivisionId(),
    courseProduct
  );
  const preserveActiveOnSave = activeDeactivationLocked;

  const defaultLanguageId = React.useMemo(() => {
    const english = languageCategories.find(
      (lang) => (lang.name ?? '').toLowerCase().includes('english')
    );
    const fallback = english ?? languageCategories[0];

    return fallback ? String(fallback.id) : undefined;
  }, [languageCategories]);

  React.useEffect(() => {
    let isMounted = true;
    api.getAllBookingSystems()
      .then(systems => {
        if (isMounted) {
          setBookingSystems(systems);
        }
      })
      .catch((err: any) => {
        message.error(err?.message ?? 'Failed to load booking systems');
      });
    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    const id = Number(courseProduct?.id ?? 0);
    const prevId = lastCourseProductIdRef.current;
    const preservedUnitTypeId = form.getFieldValue('unitTypeId');

    form.resetFields();

    const hasValidLanguageId = courseProduct?.languageId !== undefined && 
                                courseProduct?.languageId !== null && 
                                courseProduct.languageId > 0;
    const hasClosureDate = courseProduct?.closureDate != null;
    const activeFromProduct = typeof courseProduct?.active === 'boolean' ? courseProduct.active : true;
    const active = !hasClosureDate && activeFromProduct;

    let unitTypeId: number | undefined;
    if (id > 0) {
      const ut = courseProduct?.unitTypeId;
      unitTypeId = ut != null && Number(ut) > 0 ? Number(ut) : undefined;
    } else {
      const switchedFromExistingToNew = prevId !== undefined && prevId > 0 && id === 0;
      if (switchedFromExistingToNew) {
        unitTypeId = undefined;
      } else if (preservedUnitTypeId != null && preservedUnitTypeId !== '') {
        unitTypeId = Number(preservedUnitTypeId);
      } else {
        unitTypeId = undefined;
      }
    }
    lastCourseProductIdRef.current = id;

    form.setFieldsValue({
      name: courseProduct?.name || '',
      intensityId: !courseProduct?.id || !courseProduct?.intensityId ? undefined : Number(courseProduct.intensityId),
      languageId: hasValidLanguageId
        ? String(courseProduct.languageId)
        : defaultLanguageId,
      productCategory: courseProduct?.productCategoryText ? String(courseProduct.productCategoryText) : undefined,
      accountCategory: courseProduct?.accountCategoryText ? String(courseProduct.accountCategoryText) : undefined,
      productCategoryId: !courseProduct?.id || !courseProduct?.productCategoryId ? undefined : courseProduct.productCategoryId,
      accountCategoryId: !courseProduct?.id || !courseProduct?.accountCategoryId ? undefined : courseProduct.accountCategoryId,
      active,
      excludedBookingSystemIds: courseProduct?.excludedBookingSystemIds ?? [],
      minimumWeeks: courseProduct?.minimumWeeks ?? null,
      minimumAge: courseProduct?.minimumAge ?? null,
      unitTypeId,
    });
  }, [courseProduct, form, defaultLanguageId]);

  React.useEffect(() => {
    if (!courseUnitTypeOptions.length) return;
    const raw = form.getFieldValue("unitTypeId");
    if (raw == null) return;
    const exists = courseUnitTypeOptions.some(u => Number(u.id) === Number(raw));
    if (!exists) {
      form.setFieldsValue({ unitTypeId: undefined });
    }
  }, [courseUnitTypeOptions, form]);

  React.useEffect(() => {
    const hasValidLanguageId = courseProduct?.languageId !== undefined && 
                                courseProduct?.languageId !== null && 
                                courseProduct.languageId > 0;
    if (hasValidLanguageId) {
      return;
    }

    const currentValue = form.getFieldValue('languageId');
    if (!currentValue && defaultLanguageId) {
      form.setFieldsValue({ languageId: defaultLanguageId });
    }
  }, [form, defaultLanguageId, courseProduct?.languageId]);

  React.useEffect(() => {
    const productId = courseProduct?.id ?? 0;
    if (!productId) {
      setInitialExcludedSystems([]);
      form.setFieldsValue({ excludedBookingSystemIds: [] });
      setIsExclusionLoading(false);
      return;
    }

    let cancelled = false;
    setIsExclusionLoading(true);
    api.getExcludedBookingSystemIdsForProduct(productId, COURSE_PRODUCT_TYPE_ID)
      .then(ids => {
        if (!cancelled) {
          setInitialExcludedSystems(ids);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setInitialExcludedSystems([]);
          message.error(err?.message ?? 'Failed to load booking system exclusions');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsExclusionLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [courseProduct?.id, form]);

  React.useEffect(() => {
    form.setFieldsValue({ excludedBookingSystemIds: initialExcludedSystems });
  }, [initialExcludedSystems, form]);



    const toNum = (v: any) => (v === undefined || v === null ? NaN : Number(v));
    const pickId = (o: any) => toNum(o?.id ?? o?.courseIntensityId ?? o?.categoryId ?? o?.value);
    const pickName = (o: any) => (o?.name ?? o?.text ?? o?.title ?? o?.label ?? "");

    const nameById = React.useMemo(() => ({
      intensity: new Map(courseIntensity.map(i => [pickId(i), pickName(i)])),
      language:  new Map(languageCategories.map(l => [String(l.id), pickName(l)])),
      product:   new Map(productCategories.map(c => [pickId(c), pickName(c)])),
      account:   new Map(accountCategories.map(c => [pickId(c), pickName(c)])),
      unitType:  new Map(unitTypes.map(u => [pickId(u), pickName(u)])),
    }), [
      courseIntensity,
      languageCategories,
      productCategories,
      accountCategories,
      unitTypes,
    ]);


  const handleFinish = async (values: any) => {
    setIsLoading(true);
    dispatch(setFormErrors([]));
    try {
      const intensityId  = Number(values.intensityId);
      const rawLanguageId = values.languageId;
      const languageId   = Number(rawLanguageId);
      if (!Number.isFinite(languageId)) {
        message.error('Please select a valid language');
        setIsLoading(false);
        return;
      }

      const prodCatId    = Number(values.productCategoryId);
      const acctCatId    = Number(values.accountCategoryId);
      const courseIntensityText   = nameById.intensity.get(intensityId) ?? "";
      const courseLanguageText    = nameById.language.get(String(rawLanguageId)) ?? "";
      const productCategoryText   = nameById.product.get(prodCatId) ?? "";
      const accountCategoryText   = nameById.account.get(acctCatId) ?? "";

      const unitTypeIdNum = preserveUnitTypeOnSave
        ? Number(courseProduct?.unitTypeId ?? values.unitTypeId ?? 0)
        : Number(values.unitTypeId);
      const unitTypeText =
        nameById.unitType.get(unitTypeIdNum) ||
        (preserveUnitTypeOnSave ? courseProduct?.unitTypeText ?? "" : "");

      const payload: any = {
        id: courseProduct?.id || 0,
        name: values.name,
        intensityId: Number(values.intensityId),
        languageId,
        productCategoryId: values.productCategoryId,
        accountCategoryId: values.accountCategoryId,
        active: preserveActiveOnSave ? true : values.active !== false,
        closureDate: preserveActiveOnSave ? null : values.active === false ? new Date() : null,
        division: courseProduct?.division,
        courseIntensityText,
        courseLanguageText,
        productCategoryText,
        accountCategoryText,
        minimumWeeks: normalizeOptionalInt(values.minimumWeeks),
        minimumAge: normalizeOptionalInt(values.minimumAge),
        unitTypeId: unitTypeIdNum,
        unitTypeText,
      };

      const result = await api.saveCourseProduct(payload, DivisionId());

      if (result?.ok) {
        const savedProductIdRaw =
          result?.data?.productId ??
          result?.data?.id ??
          payload.id ??
          0;
        const savedProductId = Number(savedProductIdRaw);
        const selectedSystems = form.getFieldValue('excludedBookingSystemIds') as (string | number)[] | undefined;

        if (savedProductId > 0) {
          try {
            const updatedIds = await api.updateBookingSystemProductExclusions(
              savedProductId,
              COURSE_PRODUCT_TYPE_ID,
              selectedSystems,
              initialExcludedSystems
            );
            setInitialExcludedSystems(updatedIds);
          } catch (err: any) {
            message.error(err?.message ?? 'Failed to update booking system exclusions');
            return;
          }
        } else if (Array.isArray(selectedSystems) && selectedSystems.length > 0) {
          message.warning('Saved, but booking system exclusions could not be updated because the product id was unavailable.');
        }

        try {
          const safeList = await api.getCourseProducts(DivisionId()) || [];

          if(result?.data && !safeList.some(c => c.id == savedProductId))
          {
            payload.id = savedProductId || 0;
            payload.languageText = nameById.language.get(String(rawLanguageId)) ?? "";
            payload.intensityText = nameById.intensity.get(intensityId) ?? "";
            payload.productCategoryText = productCategoryText;
            payload.accountCategoryText = accountCategoryText;
            payload.active = true;
            payload.entityUpdate = true;

            safeList.push(payload);
          }

          dispatch(listCourseProductsData(safeList));
          dispatch(filterData(safeList));
        } catch { 
          
        }
        
        message.success(result.message || 'Saved successfully!');
        dispatch(getCourseProductById(result.data));
        dispatch(setCourseProductName(payload.name));
        if (intensityId != null) {
          dispatch(setCourseProductIntensity(Number(intensityId)));
        }
        onSuccess?.();
      } else {
        message.error(result?.message || 'Save failed');
      }
    } catch (error: any) {
      const apiErrors = error?.response?.errors as FormError[] | undefined;
      if (apiErrors && apiErrors.length) {
        dispatch(setFormErrors(apiErrors));
      }
      message.error(error?.message || 'Save failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card style={{ margin: '0 auto', marginTop: 25 }} styles={{ body: { paddingBottom: 0 } }}>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
  <Row gutter={16}>
    <Col span={12}>
      <Form.Item
        label="Course Product Name"
        name="name"
         rules={[
                { required: true, message: 'Please enter the course name' },
                { max: 100, message: 'Max 100 characters' },
              ]}
      >
        <Input maxLength={100} placeholder="Enter course name"  />
      </Form.Item>
    </Col>
    <Col span={12}>
      <Form.Item
        label="Language"
        name="languageId"
        rules={[{ required: true, message: 'Please select a language' }]}
      >
        <Select
          options={languageOptions}
          placeholder="Select language"
          showSearch={{ optionFilterProp: 'label' }}
          style={{ width: '100%' }}
        />
      </Form.Item>
    </Col>
  </Row>

  <Row gutter={16}>
    <Col span={12}>
      <Form.Item label="Unit Type" required>
        <Tooltip title={unitTypeLocked ? 'Has active offering' : undefined}>
          <span style={{ display: 'block', width: '100%' }}>
            <Form.Item
              name="unitTypeId"
              noStyle
              rules={[{ required: true, message: 'Please select unit type' }]}
            >
              <Select
                disabled={unitTypeLocked}
                options={courseUnitTypeOptions.map(u => ({ label: u.name, value: Number(u.id) }))}
                placeholder="Select Unit Type"
                showSearch={unitTypeLocked ? false : { optionFilterProp: 'label' }}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </span>
        </Tooltip>
      </Form.Item>
    </Col>
    <Col span={12}>
      <Form.Item
          label="Intensity"
          name="intensityId"
          rules={[{ required: true, message: 'Please select intensity' }]}
      >
        <Select
            options={intensityOptions}
            placeholder="Select Intensity"
            style={{ width: '100%' }}
            showSearch={{ optionFilterProp: 'label' }}
            allowClear
            suffixIcon={<SearchOutlined />}
            optionRender={(opt) => {
              const raw = (opt as any).data ?? opt;
              return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '2px 0' }}>
                    <div style={{ fontWeight: 500, lineHeight: 1.2 }}>
                      {raw.label}
                    </div>
                    <div
                        style={{
                          fontSize: 12,
                          color: 'rgba(0, 0, 0, 0.45)',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                    >
                      {raw.meta}
                    </div>
                  </div>
              );
            }}
        />
      </Form.Item>
    </Col>
  </Row>

  <Row gutter={16}>
    <Col span={8}>
      <Form.Item
          label="Min Weeks"
          name="minimumWeeks"
          rules={[
            {
              validator: async (_, v) => {
                if (v === null || v === undefined || v === "") return;
                const n = Number(v);
                if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
                  throw new Error("Min weeks must be a whole number ≥ 0");
                }
              },
            },
          ]}
      >
        <InputNumber
            min={0}
            precision={0}
            style={{ width: '100%' }}
            placeholder="Enter minimum number of weeks"
        />
      </Form.Item>
    </Col>

    <Col span={8}>
      <Form.Item
          label="Min Age"
          name="minimumAge"
          rules={[
            {
              validator: async (_, v) => {
                if (v === null || v === undefined || v === "") return;
                const n = Number(v);
                if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
                  throw new Error("Min age must be a whole number ≥ 0");
                }
              },
            },
          ]}
      >
        <InputNumber
            min={0}
            precision={0}
            style={{ width: '100%' }}
            placeholder="Enter minimum age"
        />
      </Form.Item>
    </Col>

    <Col span={8}>
      <Form.Item
        label="Product Category"
        name="productCategoryId"
        rules={[{ required: true, message: 'Please select a product category' }]}
      >
        <Select
         options={productCategoryOptions}
          placeholder="Select Product Category"
          showSearch={{ optionFilterProp: 'label' }}
          style={{ width: '100%' }}
        />
      </Form.Item>
    </Col>
  </Row>

  <Row gutter={16}>
    <Col span={12}>
      <Form.Item
        label="Account Category"
        name="accountCategoryId"
         rules={[{ required: true, message: 'Please select an account category' }]}
      >
        <Select
          options={accountCategoryOptions}
          placeholder="Select Account Category"
          showSearch={{ optionFilterProp: 'label' }}
          style={{ width: '100%' }}
        />
      </Form.Item>
    </Col>
  </Row>
  <Row gutter={16}>
     <Col span={12}>
            <ActiveStatusFormItem
              name="active"
              style={{ marginTop: 26 }}
              tooltip={activeDeactivationLocked ? PRODUCT_ACTIVE_PACKAGE_TOOLTIP : undefined}
              disabled={activeDeactivationLocked}
            />
          </Col>
  </Row>
  <Row>
    <Col span={24}>
      <Form.Item
        name="excludedBookingSystemIds"
        label={
          <>
            <span style={{ fontWeight: 'bold', color: 'red', marginRight: 3 }}>Exclude</span>
            {' this course product from the following systems:'}
          </>
        }
      >
        <Checkbox.Group
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          disabled={isExclusionLoading}
        >
          {bookingSystems.map(system => (
            <Checkbox key={system.id} value={String(system.id)}>
              {system.name}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>
    </Col>
  </Row>

  <Form.Item style={{ textAlign: 'right', marginTop: 18 }}>
    <Button type="primary" htmlType="submit" loading={isLoading} size="small" style={{ minWidth: 120, marginRight: 10 }}>
      Save
    </Button>
  </Form.Item>
</Form>
    </Card>
  );
};

export default AddEditCourseForm;